// src/pages/DealsListPage.tsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealsApi, accountsApi } from '../api';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, CircularProgress, Box, Button, TextField, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Link,
  Chip, IconButton, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Pagination, Alert, Card, CardContent
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useState, useMemo } from 'react';
import {
  Add as AddIcon, Search as SearchIcon, AttachMoney as MoneyIcon,
  Business as BusinessIcon, TrendingUp as TrendingUpIcon,
  MeetingRoom as RoomIcon, Visibility as ViewIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import type { DealReadWithInsights, DealCreate, Account, DealStage } from '../types/api';

interface DealFormData {
  name: string;
  amount: number;
  account_id: string;
  stage: DealStage;
  description: string;
}

interface DealFilters {
  search: string;
  account_id: string;
  stage: string;
  page: number;
  limit: number;
}

const ITEMS_PER_PAGE = 25;

const DEAL_STAGES: DealStage[] = [
  'Prospecting',
  'Qualification',
  'Proposal',
  'Negotiation',
  'Closed Won',
  'Closed Lost'
];

// Helper function to get stage color
const getStageColor = (stage: DealStage): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (stage) {
    case 'Prospecting': return 'info';
    case 'Qualification': return 'primary';
    case 'Proposal': return 'warning';
    case 'Negotiation': return 'secondary';
    case 'Closed Won': return 'success';
    case 'Closed Lost': return 'error';
    default: return 'default';
  }
};

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export function DealsListPage() {
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<DealFormData>({
    name: '',
    amount: 0,
    account_id: '',
    stage: 'Prospecting',
    description: ''
  });
  const [formError, setFormError] = useState('');
  const [filters, setFilters] = useState<DealFilters>({
    search: '',
    account_id: '',
    stage: '',
    page: 1,
    limit: ITEMS_PER_PAGE
  });

  const queryClient = useQueryClient();

  // Fetch deals with filtering
  const { data: deals, isLoading, isError, error, refetch } = useQuery<DealReadWithInsights[]>({
    queryKey: ['deals', filters],
    queryFn: async () => {
      try {
        console.log('Fetching deals with filters:', filters);
        const apiFilters: any = {
          skip: (filters.page - 1) * filters.limit,
          limit: filters.limit
        };

        if (filters.search) {
          apiFilters.q = filters.search;
        }

        if (filters.account_id) {
          apiFilters.account_id = filters.account_id;
        }

        if (filters.stage) {
          apiFilters.stage = filters.stage;
        }

        return await dealsApi.getDeals(apiFilters);
      } catch (error) {
        console.error('Error fetching deals:', error);
        throw error;
      }
    },
    retry: 1,
    onError: (err: any) => {
      console.error('Query error:', err);
      setErrorDetails(
        `Status: ${err?.response?.status || 'Unknown'}\n` +
        `Message: ${err?.message || 'Unknown error'}\n` +
        `Response: ${JSON.stringify(err?.response?.data || {}, null, 2)}`
      );
    }
  });

  // Fetch accounts for the dropdown
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: async () => {
      try {
        return await accountsApi.getAccounts();
      } catch (error) {
        console.error('Error fetching accounts:', error);
        return [];
      }
    }
  });

  // Create deal mutation
  const createDealMutation = useMutation({
    mutationFn: async (dealData: DealCreate) => {
      return await dealsApi.createDeal(dealData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      setDialogOpen(false);
      setFormData({
        name: '',
        amount: 0,
        account_id: '',
        stage: 'Prospecting',
        description: ''
      });
      setFormError('');
    },
    onError: (error: any) => {
      console.error('Error creating deal:', error);
      setFormError(error?.response?.data?.detail || 'Failed to create deal');
    }
  });

  // Calculate pipeline metrics
  const pipelineMetrics = useMemo(() => {
    if (!deals) return { totalValue: 0, totalDeals: 0, avgDealSize: 0, stageBreakdown: {} };

    const totalValue = deals.reduce((sum, deal) => sum + deal.amount, 0);
    const totalDeals = deals.length;
    const avgDealSize = totalDeals > 0 ? totalValue / totalDeals : 0;

    const stageBreakdown = deals.reduce((acc, deal) => {
      acc[deal.stage] = (acc[deal.stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { totalValue, totalDeals, avgDealSize, stageBreakdown };
  }, [deals]);

  // Handle form submission
  const handleSubmit = () => {
    if (!formData.name || !formData.account_id || formData.amount <= 0) {
      setFormError('Name, Account, and Amount (greater than 0) are required');
      return;
    }

    const dealData: DealCreate = {
      name: formData.name.trim(),
      amount: formData.amount,
      account_id: formData.account_id,
      stage: formData.stage,
      description: formData.description.trim() || undefined
    };

    createDealMutation.mutate(dealData);
  };

  // Handle search
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  // Handle filters
  const handleAccountFilterChange = (accountId: string) => {
    setFilters(prev => ({ ...prev, account_id: accountId, page: 1 }));
  };

  const handleStageFilterChange = (stage: string) => {
    setFilters(prev => ({ ...prev, stage: stage, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters(prev => ({ ...prev, page: value }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load deals. Please try again.
        </Alert>
        <Button onClick={() => refetch()} variant="contained">
          Retry
        </Button>
        {errorDetails && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
              {errorDetails}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Sales Pipeline
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Deal
        </Button>
      </Box>

      {/* Pipeline Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h6">{formatCurrency(pipelineMetrics.totalValue)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Pipeline</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" fontSize="large" />
                <Box>
                  <Typography variant="h6">{pipelineMetrics.totalDeals}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Deals</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="info" fontSize="large" />
                <Box>
                  <Typography variant="h6">{formatCurrency(pipelineMetrics.avgDealSize)}</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Deal Size</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FilterIcon color="secondary" fontSize="large" />
                <Box>
                  <Typography variant="h6">{Object.keys(pipelineMetrics.stageBreakdown).length}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Stages</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search deals"
              placeholder="Search by name or description"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Account</InputLabel>
              <Select
                value={filters.account_id}
                label="Filter by Account"
                onChange={(e) => handleAccountFilterChange(e.target.value)}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Stage</InputLabel>
              <Select
                value={filters.stage}
                label="Filter by Stage"
                onChange={(e) => handleStageFilterChange(e.target.value)}
              >
                <MenuItem value="">All Stages</MenuItem>
                {DEAL_STAGES.map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {deals?.length || 0} deal{(deals?.length || 0) !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Deals Table */}
      <Paper className="deals-list pipeline" data-testid="pipeline">
        <Table data-testid="deals-table">
          <TableHead>
            <TableRow>
              <TableCell>Deal Name</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!deals || deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {filters.search || filters.account_id || filters.stage ? 'No deals match your filters' : 'No deals found'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {!filters.search && !filters.account_id && !filters.stage && 'Create your first deal to get started'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              deals.map((deal) => (
                <TableRow key={deal.id} hover>
                  <TableCell>
                    <Box>
                      <Link
                        component={RouterLink}
                        to={`/deals/${deal.id}`}
                        sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                      >
                        {deal.name}
                      </Link>
                      {deal.insights && deal.insights.length > 0 && (
                        <Chip
                          label={`${deal.insights.length} insights`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(deal.amount)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deal.stage}
                      size="small"
                      color={getStageColor(deal.stage)}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Link
                        component={RouterLink}
                        to={`/accounts/${deal.account_id}`}
                        sx={{ textDecoration: 'none' }}
                      >
                        Account {deal.account_id.slice(-8)}
                      </Link>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(deal.created_at).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View deal details">
                        <IconButton
                          component={RouterLink}
                          to={`/deals/${deal.id}`}
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open deal room">
                        <IconButton
                          component={RouterLink}
                          to={`/deals/${deal.id}/room`}
                          size="small"
                        >
                          <RoomIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      {deals && deals.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(deals.length / ITEMS_PER_PAGE)}
            page={filters.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create Deal Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Deal</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deal Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!formData.name && formError.includes('Name')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                error={formData.amount <= 0 && formError.includes('Amount')}
                InputProps={{
                  startAdornment: '$'
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Stage</InputLabel>
                <Select
                  value={formData.stage}
                  label="Stage"
                  onChange={(e) => setFormData({ ...formData, stage: e.target.value as DealStage })}
                >
                  {DEAL_STAGES.map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      {stage}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!formData.account_id && formError.includes('Account')}>
                <InputLabel>Account *</InputLabel>
                <Select
                  value={formData.account_id}
                  label="Account *"
                  onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createDealMutation.isLoading}
          >
            {createDealMutation.isLoading ? 'Creating...' : 'Create Deal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
